import { type ReactNode, useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function Modal({ open, onClose, title, children, size = 'md' }: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className={`${sizeClasses[size]} w-full rounded-xl border-0 bg-white p-0 shadow-xl backdrop:bg-black/50`}
    >
      <div className="flex items-center justify-between border-b border-silver-200 px-6 py-4">
        <h2 className="text-lg font-semibold text-silver-900">{title}</h2>
        <button onClick={onClose} className="rounded-lg p-1 text-silver-400 hover:bg-silver-100 hover:text-silver-600">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <div className="px-6 py-4">{children}</div>
    </dialog>
  );
}
